import { RefObject, ReactNode, CSSProperties, KeyboardEvent } from "react";
import { clsx } from "clsx";
import EditableField from "@/common/EditableField";

export default function Panel({
    children,
    title,
    subtitle,
    action,
    className,
    compact = false,
    borderless = false,
    flush = false,
    ref,
    onTitleChange,
    onClick,
    onKeyDown,
    role,
    tabIndex,
    style,
}: {
    children: ReactNode;
    title?: ReactNode;
    subtitle?: ReactNode;
    action?: ReactNode;
    className?: string;
    compact?: boolean;
    /**
     * Drops the border, shadow and padding, keeping only the title row.
     *
     * For content that draws its own container -- a data grid has a hairline
     * border of its own, and nesting it inside the panel's produces two rules a
     * few pixels apart. The panel is then a heading and an action row rather
     * than a card, which is what it is being asked to be.
     *
     * A prop rather than a `className` override because the border classes are
     * applied here after `className`, so a caller cannot win on specificity.
     */
    borderless?: boolean;
    /**
     * Keeps the card -- border, shadow, radius -- but drops the padding.
     *
     * For content that runs to the edges of the card: a split view whose
     * divider has to meet the border, a list whose rows highlight full-bleed.
     * Padding is then the content's business rather than the panel's. Like
     * `borderless` this is a prop and not a `className` override, because the
     * padding classes are applied here after `className`.
     */
    flush?: boolean;
    ref?: RefObject<HTMLDivElement | null>;
    onTitleChange?: (newTitle?: string) => void;
    onClick?: () => void;
    onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
    role?: string;
    tabIndex?: number;
    style?: CSSProperties;
}) {
    return (
        <div
            ref={ref}
            onClick={onClick}
            onKeyDown={onKeyDown}
            role={role}
            tabIndex={tabIndex}
            className={clsx(
                className,
                "bg-transparent",
                !borderless &&
                    // The site's card edge: Grey 200 (zinc-200), no shadow
                    "rounded-xl border-zinc-200 dark:border-white/10 border-solid border",
                // A flush panel is a frame around content that sizes itself,
                // so the frame has to pass its own height down rather than
                // wrap whatever the content happens to be.
                flush && "flex flex-col overflow-hidden",
                // Borderless emits NO padding class, so the caller's own
                // padding in `className` is not competing with one of ours at
                // equal specificity -- which is decided by stylesheet order
                // rather than by anything visible here.
                !borderless && !flush && (compact ? "p-2.5" : "p-6"),
            )}
            style={style}
        >
            <div
                className={clsx(
                    "flex flex-col text-default h-full min-h-0",
                    flush && "grow",
                )}
            >
                {/* The gap below the header belongs to the ROW, not to the
                    title inside it. While it sat on the title, the action beside
                    it had no margin of its own, so `items-center` centred the
                    action against the title PLUS its 24px -- landing it below
                    the words it sits next to rather than level with them. */}
                <div
                    className={clsx(
                        "flex items-center justify-between shrink-0",
                        // Tighter on a borderless panel. With no card around
                        // them the title is a caption for content that draws
                        // its own frame, and a caption sits close to the thing
                        // it names -- a card header has a border to span, so it
                        // can afford the distance.
                        title && (borderless ? "mb-3" : "mb-6"),
                    )}
                >
                    {title && (
                        <div>
                            {!onTitleChange && (
                                <h2 className="text-lg font-semibold leading-6 tracking-tight">
                                    {title}
                                </h2>
                            )}
                            {onTitleChange && typeof title === "string" && (
                                <EditableField
                                    isTitle={true}
                                    value={title}
                                    onSave={onTitleChange}
                                />
                            )}
                            {subtitle && (
                                <div className="mt-1 text-sm text-subtle">
                                    {subtitle}
                                </div>
                            )}
                        </div>
                    )}
                    {action}
                </div>
                <div
                    className={clsx(
                        "text-sm h-full min-h-0",
                        flush && "grow flex flex-col",
                    )}
                >
                    {children}
                </div>
            </div>
        </div>
    );
}
